import ngeohash from "ngeohash";
import { GeoPoint } from "firebase/firestore";

import { GOOGLE_MAPS_API_KEY } from "./googleMapsLoader";

// Hayes, Kansas default coordinates
const DEFAULT_LOCATION = {
  latitude: 39.3483,
  longitude: -99.3287,
  location: "Hayes, Kansas, USA",
  locationDisplay: "Hayes, KS",
  locationCountry: "United States"
};

/**
 * Parse address components from Google Places result
 */
const parseAddressComponents = (addressComponents) => {
  let city = "";
  let state = "";
  let country = "";
  
  addressComponents.forEach((component) => {
    if (component.types.includes("locality")) {
      city = component.long_name;
    }
    if (component.types.includes("administrative_area_level_1")) {
      state = component.short_name; // Use short_name for state abbreviation
    }
    if (component.types.includes("country")) {
      country = component.long_name;
    }
  });
  
  return { city, state, country };
};

/**
 * Reverse geocode coordinates to get location information
 */
export const reverseGeocode = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
    );
    
    const data = await response.json();
    
    if (data.status === "OK" && data.results && data.results.length > 0) {
      const result = data.results[0];
      const { city, state, country } = parseAddressComponents(result.address_components);
      
      return {
        location: result.formatted_address,
        locationDisplay: city && state ? `${city}, ${state}` : result.formatted_address,
        locationCountry: country
      };
    }
    
    // Fallback if geocoding doesn't return good results
    return {
      location: `${lat}, ${lng}`,
      locationDisplay: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
      locationCountry: ""
    };
  } catch (error) {
    console.error("Error in reverse geocoding:", error);
    // Return basic format on error
    return {
      location: `${lat}, ${lng}`,
      locationDisplay: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
      locationCountry: ""
    };
  }
};

/**
 * Forward geocode address to get coordinates
 */
export const forwardGeocode = async (address) => {
  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}`
    );
    
    const data = await response.json();
    
    if (data.status === "OK" && data.results && data.results.length > 0) {
      const result = data.results[0];
      const { lat, lng } = result.geometry.location;
      return { latitude: lat, longitude: lng };
    }
    
    return null;
  } catch (error) {
    console.error("Error in forward geocoding:", error);
    return null;
  }
};

/**
 * Convert coordinates to all required location formats
 */
export const getLocationFromCoordinates = async (lat, lng) => {
  try {
    const geohash = ngeohash.encode(lat, lng);
    const geocodeResult = await reverseGeocode(lat, lng);
    
    return {
      coordinates: new GeoPoint(lat, lng),
      g: {
        geohash: geohash,
        geopoint: new GeoPoint(lat, lng)
      },
      location: geocodeResult.location,
      locationDisplay: geocodeResult.locationDisplay,
      locationCountry: geocodeResult.locationCountry
    };
  } catch (error) {
    console.error("Error getting location from coordinates:", error);
    throw error;
  }
};

/**
 * Convert geohash to all required location formats
 */
export const getLocationFromGeohash = async (geohash) => {
  try {
    const decoded = ngeohash.decode(geohash);
    return await getLocationFromCoordinates(decoded.latitude, decoded.longitude);
  } catch (error) {
    console.error("Error getting location from geohash:", error);
    throw error;
  }
};

/**
 * Convert geopoint to all required location formats
 */
export const getLocationFromGeopoint = async (geopoint) => {
  try {
    const lat = geopoint._lat || geopoint.latitude;
    const lng = geopoint._long || geopoint.longitude;
    return await getLocationFromCoordinates(lat, lng);
  } catch (error) {
    console.error("Error getting location from geopoint:", error);
    throw error;
  }
};

/**
 * Convert address string to all required location formats
 */
export const getLocationFromAddress = async (address) => {
  try {
    const coords = await forwardGeocode(address);
    if (coords) {
      return await getLocationFromCoordinates(coords.latitude, coords.longitude);
    }
    return null;
  } catch (error) {
    console.error("Error getting location from address:", error);
    throw error;
  }
};

/**
 * Get location from organization data (checks all fields in priority order)
 */
export const getLocationFromOrganization = async (orgData) => {
  if (!orgData) return null;
  
  try {
    const hasStoredLocationStrings =
      typeof orgData.location === "string" && orgData.location.trim().length > 0;
    const hasStoredDisplayLocationStrings =
      typeof orgData.locationDisplay === "string" &&
      orgData.locationDisplay.trim().length > 0;

    // Priority 1: coordinates
    if (orgData.coordinates && 
        orgData.coordinates._lat !== 0 && 
        orgData.coordinates._long !== 0) {
      console.log("Using organization coordinates");
      const locationData = await getLocationFromCoordinates(
        orgData.coordinates._lat, 
        orgData.coordinates._long
      );

      // IMPORTANT:
      // If the Organization Profile has explicit Location / Display Location strings saved,
      // prefer those over reverse-geocoded values (which can differ slightly from Places).
      return {
        ...locationData,
        ...(hasStoredLocationStrings ? { location: orgData.location } : {}),
        ...(hasStoredDisplayLocationStrings
          ? { locationDisplay: orgData.locationDisplay }
          : {}),
        ...(typeof orgData.locationCountry === "string" &&
        orgData.locationCountry.trim().length > 0
          ? { locationCountry: orgData.locationCountry }
          : {}),
      };
    }
    
    // Priority 2: g.geohash
    if (orgData.g && orgData.g.geohash && orgData.g.geohash.length > 0) {
      console.log("Using organization geohash");
      const locationData = await getLocationFromGeohash(orgData.g.geohash);
      return {
        ...locationData,
        ...(hasStoredLocationStrings ? { location: orgData.location } : {}),
        ...(hasStoredDisplayLocationStrings
          ? { locationDisplay: orgData.locationDisplay }
          : {}),
        ...(typeof orgData.locationCountry === "string" &&
        orgData.locationCountry.trim().length > 0
          ? { locationCountry: orgData.locationCountry }
          : {}),
      };
    }
    
    // Priority 3: g.geopoint
    if (orgData.g && 
        orgData.g.geopoint && 
        orgData.g.geopoint._lat !== 0 && 
        orgData.g.geopoint._long !== 0) {
      console.log("Using organization geopoint");
      const locationData = await getLocationFromGeopoint(orgData.g.geopoint);
      return {
        ...locationData,
        ...(hasStoredLocationStrings ? { location: orgData.location } : {}),
        ...(hasStoredDisplayLocationStrings
          ? { locationDisplay: orgData.locationDisplay }
          : {}),
        ...(typeof orgData.locationCountry === "string" &&
        orgData.locationCountry.trim().length > 0
          ? { locationCountry: orgData.locationCountry }
          : {}),
      };
    }
    
    // Priority 4: location (address string)
    if (orgData.location && orgData.location.trim().length > 0) {
      console.log("Using organization location address");
      return await getLocationFromAddress(orgData.location);
    }
    
    console.log("No valid location found in organization data");
    return null;
  } catch (error) {
    console.error("Error extracting location from organization:", error);
    return null;
  }
};

/**
 * Get location from browser geolocation API
 */
export const getLocationFromBrowser = async () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser"));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const locationData = await getLocationFromCoordinates(
            position.coords.latitude,
            position.coords.longitude
          );
          resolve(locationData);
        } catch (error) {
          reject(error);
        }
      },
      (error) => {
        reject(error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

/**
 * Get location from IP address using ipapi.co
 */
export const getLocationFromIP = async () => {
  try {
    const response = await fetch("https://ipapi.co/json/");
    const data = await response.json();
    
    if (data.latitude && data.longitude) {
      const locationData = await getLocationFromCoordinates(
        data.latitude,
        data.longitude
      );
      return locationData;
    }
    
    throw new Error("Unable to get location from IP");
  } catch (error) {
    console.error("Error getting location from IP:", error);
    throw error;
  }
};

/**
 * Get default location (Hayes, Kansas)
 */
export const getDefaultLocation = async () => {
  try {
    const locationData = await getLocationFromCoordinates(
      DEFAULT_LOCATION.latitude,
      DEFAULT_LOCATION.longitude
    );
    
    // Override with our specific display values for Hayes, Kansas
    return {
      ...locationData,
      location: DEFAULT_LOCATION.location,
      locationDisplay: DEFAULT_LOCATION.locationDisplay,
      locationCountry: DEFAULT_LOCATION.locationCountry
    };
  } catch (error) {
    console.error("Error getting default location:", error);
    // Return hardcoded values if even the default fails
    const geohash = ngeohash.encode(DEFAULT_LOCATION.latitude, DEFAULT_LOCATION.longitude);
    return {
      coordinates: new GeoPoint(DEFAULT_LOCATION.latitude, DEFAULT_LOCATION.longitude),
      g: {
        geohash: geohash,
        geopoint: new GeoPoint(DEFAULT_LOCATION.latitude, DEFAULT_LOCATION.longitude)
      },
      location: DEFAULT_LOCATION.location,
      locationDisplay: DEFAULT_LOCATION.locationDisplay,
      locationCountry: DEFAULT_LOCATION.locationCountry
    };
  }
};

/**
 * Check if organization has valid location data
 */
export const hasValidOrgLocation = (orgData) => {
  if (!orgData) return false;
  
  // Check coordinates
  if (orgData.coordinates && 
      orgData.coordinates._lat !== 0 && 
      orgData.coordinates._long !== 0) {
    return true;
  }
  
  // Check geohash
  if (orgData.g && orgData.g.geohash && orgData.g.geohash.length > 0) {
    return true;
  }
  
  // Check geopoint
  if (orgData.g && 
      orgData.g.geopoint && 
      orgData.g.geopoint._lat !== 0 && 
      orgData.g.geopoint._long !== 0) {
    return true;
  }
  
  // Check location string
  if (orgData.location && orgData.location.trim().length > 0) {
    return true;
  }
  
  return false;
};
